import gql from 'graphql-tag';

const RELEASE_BUNDLE_FRAGMENT = gql`
  fragment ReleaseBundleFields on ReleaseBundle {
    id
    name
    ehubIds
    version
    releaseChannels
    lastBuildNumber
    releaseNotes {
      info
      features {
        title
        description
      }
      fixes {
        title
        description
      }
    }
    packages {
      active
      name
      upgradePackageId
      upgradePackageTemplateId
      version
      releaseNotes {
        info
        features {
          title
          description
        }
        fixes {
          title
          description
        }
      }
    }
  }
`;

const RELEASE_BUNDLE_WITH_INFO_FRAGMENT = gql`
  fragment ReleaseBundleWithInfoFields on ReleaseBundle {
    ...ReleaseBundleFields
    info {
      packageInfo {
        upgradePackageTemplateId
        prevReleaseVersion
        newPatchVersion
        newLatestVersion
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

const RELEASE_BUNDLE_BUILD_FRAGMENT = gql`
  fragment ReleaseBundleBuildFields on ReleaseBundleBuild {
    id
    publicationStatus
    version
    versionMajor
    versionMinor
    versionPatch
    buildNumber
    releaseBundle {
      ...ReleaseBundleFields
    }
    updatedAt
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

const RELEASE_BUNDLE_BUILD_WITH_INFO_FRAGMENT = gql`
  fragment ReleaseBundleBuildWithInfoFields on ReleaseBundleBuild {
    id
    publicationStatus
    version
    versionMajor
    versionMinor
    versionPatch
    buildNumber
    releaseBundle {
      ...ReleaseBundleWithInfoFields
    }
    updatedAt
  }
  ${RELEASE_BUNDLE_WITH_INFO_FRAGMENT}
`;

export const GET_UPGRADE_PACKAGE_QUERY = gql`
  query getUpgradePackage($id: ID) {
    getUpgradePackage(id: $id) {
      id
      unitTypeId
      productId
      applicationId
      version
      binaryFileId
      tags
    }
  }
`;

export const GET_UPGRADE_PACKAGE_TEMPLATE_QUERY = gql`
  query getUpgradePackageTemplate($id: ID) {
    getUpgradePackageTemplate(id: $id) {
      id
      name
      unitTypeId
      productId
      applicationId
      upgradePackages {
        id
        unitTypeId
        productId
        applicationId
        version
        binaryFileId
        tags
      }
    }
  }
`;

export const GET_ALL_UPGRADE_PACKAGE_TEMPLATES = gql`
  query getAllUpgradePackageTemplates {
    getAllUpgradePackageTemplates {
      id
      name
    }
  }
`;

export const GET_UPGRADE_PACKAGES_FOR_TEMPLATE = gql`
  query getUpgradePackagesForTemplate($upgradePackageTemplateId: ID!) {
    getUpgradePackagesForTemplate(
      upgradePackageTemplateId: $upgradePackageTemplateId
    ) {
      id
      unitTypeId
      productId
      applicationId
      version
      binaryFileId
      tags
    }
  }
`;

export const GET_RELEASE_FILE_QUERY = gql`
  query getReleaseFile($id: ID) {
    getReleaseFile(id: $id) {
      id
      unitTypeId
      productId
      applicationId
      version
      binaryFileId
    }
  }
`;

export const SET_UPGRADE_PACKAGE_TAG = gql`
  mutation setUpgradePackageTag($upgradePackageId: ID, $tag: String) {
    setUpgradePackageTag(upgradePackageId: $upgradePackageId, tag: $tag) {
      id
      unitTypeId
      productId
      version
      binaryFileId
      tags
    }
  }
`;

export const CLEAR_UPGRADE_PACKAGE_TAG = gql`
  mutation clearUpgradePackageTag($upgradePackageId: ID, $tag: String) {
    clearUpgradePackageTag(upgradePackageId: $upgradePackageId, tag: $tag) {
      id
      unitTypeId
      productId
      version
      binaryFileId
      tags
    }
  }
`;

export const GENERATE_UPGRADE_PACKAGE = gql`
  mutation generateUpgradePackage(
    $upgradePackageTemplateId: ID!
    $versionFilter: String
    $versionOverride: String
  ) {
    generateUpgradePackage(
      upgradePackageTemplateId: $upgradePackageTemplateId
      versionFilter: $versionFilter
      versionOverride: $versionOverride
    ) {
      id
      unitTypeId
      productId
      version
      binaryFileId
      tags
    }
  }
`;

export const ADD_UPGRADE_PACKAGE_TEMPLATE = gql`
  mutation addOrUpdateUpgradePackageTemplate(
    $upgradePackageTemplate: UpgradePackageTemplateInput!
  ) {
    addOrUpdateUpgradePackageTemplate(
      upgradePackageTemplate: $upgradePackageTemplate
    ) {
      id
      name
    }
  }
`;

export const DELETE_UPGRADE_PACKAGE_TEMPLATE = gql`
  mutation deleteUpgradePackageTemplate($id: ID!) {
    deleteUpgradePackageTemplate(id: $id)
  }
`;

export const DELETE_UPGRADE_PACKAGE = gql`
  mutation deleteUpgradePackage($id: ID!) {
    deleteUpgradePackage(id: $id)
  }
`;

export const DELETE_RELEASE_FILE = gql`
  mutation deleteReleaseFile($id: ID!) {
    deleteReleaseFile(id: $id)
  }
`;

export const CREATE_RELEASE_BUNDLE = gql`
  mutation createReleaseBundle(
    $version: String!
    $name: String!
    $releaseChannels: [String!]!
  ) {
    createReleaseBundle(
      version: $version
      name: $name
      releaseChannels: $releaseChannels
    ) {
      result
      details
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const GET_ALL_RELEASE_BUNDLES = gql`
  query getAllReleaseBundles {
    getAllReleaseBundles {
      ...ReleaseBundleFields
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const GET_ALL_RELEASE_BUNDLES_MINIMAL = gql`
  query getAllReleaseBundles($includeInfo: Boolean) {
    getAllReleaseBundles(includeInfo: $includeInfo) {
      id
      name
      ehubIds
      version
      releaseChannels
      lastBuildNumber
      info {
        changesSinceLatestRelease
      }
    }
  }
`;

export const GET_RELEASE_BUNDLE_BUILD = gql`
  query getReleaseBundleBuild(
    $releaseBundleBuildId: ID!
    $includeInfo: Boolean
  ) {
    getReleaseBundleBuild(
      releaseBundleBuildId: $releaseBundleBuildId
      includeInfo: $includeInfo
    ) {
      ...ReleaseBundleBuildWithInfoFields
    }
  }
  ${RELEASE_BUNDLE_BUILD_WITH_INFO_FRAGMENT}
`;

export const GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL = gql`
  query getAllReleaseBundleBuilds($releaseBundleId: ID!) {
    getAllReleaseBundleBuilds(releaseBundleId: $releaseBundleId) {
      id
      publicationStatus
      version
      versionMajor
      versionMinor
      versionPatch
      buildNumber
      releaseBundle {
        id
        name
        ehubIds
        version
        releaseChannels
        lastBuildNumber
      }
      updatedAt
    }
  }
`;

export const GET_RELEASE_BUNDLE = gql`
  query getReleaseBundle($id: ID!, $includeInfo: Boolean) {
    getReleaseBundle(id: $id, includeInfo: $includeInfo) {
      ...ReleaseBundleWithInfoFields
    }
  }
  ${RELEASE_BUNDLE_WITH_INFO_FRAGMENT}
`;

export const DELETE_RELEASE_BUNDLE = gql`
  mutation deleteReleaseBundle($releaseBundleId: ID!) {
    deleteReleaseBundle(releaseBundleId: $releaseBundleId) {
      result
      details
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const UPDATE_RELEASE_NOTES_TO_RELEASE_BUNDLE = gql`
  mutation updateReleaseNotes(
    $releaseBundleId: ID!
    $releaseNotes: ReleaseNotesInput!
  ) {
    updateReleaseNotes(
      releaseBundleId: $releaseBundleId
      releaseNotes: $releaseNotes
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const UPDATE_RELEASE_CHANNELS = gql`
  mutation updateReleaseChannels(
    $releaseBundleId: ID!
    $releaseChannels: [String!]!
  ) {
    updateReleaseChannels(
      releaseBundleId: $releaseBundleId
      releaseChannels: $releaseChannels
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const UPDATE_ALL_PACKAGES_TO_LATEST_PATCH = gql`
  mutation updateAllPackagesToLatestPatch($releaseBundleId: ID!) {
    updateAllPackagesToLatestPatch(releaseBundleId: $releaseBundleId) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const UPDATE_SINGLE_PACKAGE_TO_LATEST_PATCH = gql`
  mutation updateSinglePackageToLatestPatch(
    $releaseBundleId: ID!
    $upgradePackageTemplateId: ID!
  ) {
    updateSinglePackageToLatestPatch(
      releaseBundleId: $releaseBundleId
      upgradePackageTemplateId: $upgradePackageTemplateId
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const SET_UPGRADE_PACKAGE_FOR_RELEASE = gql`
  mutation setUpgradePackageForRelease(
    $releaseBundleId: ID!
    $upgradePackageTemplateId: ID!
    $upgradePackageId: ID!
  ) {
    setUpgradePackageForRelease(
      releaseBundleId: $releaseBundleId
      upgradePackageTemplateId: $upgradePackageTemplateId
      upgradePackageId: $upgradePackageId
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const ADD_NEW_PACKAGE_TO_RELEASE_BUNDLE = gql`
  mutation addNewPackageToReleaseBundle(
    $releaseBundleId: ID!
    $upgradePackageTemplateId: ID!
    $upgradePackageId: ID!
  ) {
    addNewPackageToReleaseBundle(
      releaseBundleId: $releaseBundleId
      upgradePackageTemplateId: $upgradePackageTemplateId
      upgradePackageId: $upgradePackageId
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const UPDATE_PACKAGE_ACTIVE_STATE = gql`
  mutation updatePackageActiveState(
    $releaseBundleId: ID!
    $upgradePackageTemplateId: ID!
    $active: Boolean!
  ) {
    updatePackageActiveState(
      releaseBundleId: $releaseBundleId
      upgradePackageTemplateId: $upgradePackageTemplateId
      active: $active
    ) {
      result
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;

export const INITIATE_RELEASE_BUNDLE_BUILD_DOWNLOAD = gql`
  mutation initiateReleaseBundleBuildDownload(
    $releaseBundleBuildId: ID!
    $ownerId: ID
  ) {
    initiateReleaseBundleBuildDownload(
      releaseBundleBuildId: $releaseBundleBuildId
      ownerId: $ownerId
    )
  }
`;

export const GET_BUNDLE_GENERATION_STATUS = gql`
  query getBundleGenerationStatus($bundleGenerationId: ID!) {
    getBundleGenerationStatus(bundleGenerationId: $bundleGenerationId) {
      bundleGenerationId
      result
      details
      fileSizeInBytes
    }
  }
`;

export const GENERATE_RELEASE_BUNDLE_BUILD = gql`
  mutation generateReleaseBundleBuild(
    $releaseBundleId: ID!
    $version: String!
    $publicationStatus: PublicationStatus!
  ) {
    generateReleaseBundleBuild(
      releaseBundleId: $releaseBundleId
      version: $version
      publicationStatus: $publicationStatus
    ) {
      result
      details
      releaseBundleBuild {
        ...ReleaseBundleBuildFields
      }
    }
  }
  ${RELEASE_BUNDLE_BUILD_FRAGMENT}
`;

export const GET_NEXT_RELEASE_BUNDLE_BUILD_VERSION = gql`
  query getNextReleaseBundleBuildVersion($releaseBundleId: ID!) {
    getNextReleaseBundleBuildVersion(releaseBundleId: $releaseBundleId)
  }
`;

export const DELETE_RELEASE_BUNDLE_BUILD = gql`
  mutation deleteReleaseBundleBuild($releaseBundleBuildId: ID!) {
    deleteReleaseBundleBuild(releaseBundleBuildId: $releaseBundleBuildId) {
      result
      details
    }
  }
`;

export const DELETE_ALL_INTERNAL_RELEASE_BUNDLE_BUILDS = gql`
  mutation deleteAllInternalReleaseBundleBuilds($releaseBundleId: ID!) {
    deleteAllInternalReleaseBundleBuilds(releaseBundleId: $releaseBundleId)
  }
`;

export const UPDATE_RELEASE_BUNDLE_BUILD_PUBLICATION_STATUS = gql`
  mutation updateReleaseBundleBuildPublicationStatus(
    $releaseBundleBuildId: ID!
    $publicationStatus: PublicationStatus!
  ) {
    updateReleaseBundleBuildPublicationStatus(
      releaseBundleBuildId: $releaseBundleBuildId
      publicationStatus: $publicationStatus
    ) {
      result
      details
      releaseBundleBuild {
        ...ReleaseBundleBuildFields
      }
    }
  }
  ${RELEASE_BUNDLE_BUILD_FRAGMENT}
`;

export const UPDATE_RELEASE_BUNDLE_INFO = gql`
  mutation updateReleaseBundleInfo(
    $id: ID!
    $version: String!
    $name: String!
    $releaseChannels: [String!]!
  ) {
    updateReleaseBundleInfo(
      id: $id
      version: $version
      name: $name
      releaseChannels: $releaseChannels
    ) {
      result
      details
      releaseBundle {
        ...ReleaseBundleFields
      }
    }
  }
  ${RELEASE_BUNDLE_FRAGMENT}
`;
